import React, { useState, useMemo, useEffect, useRef } from "react";
import { CustomerViewEditViewFullInfoProps } from "./customer-view-edit-view-full-info.props";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import { LEAD_OWNER_OPTIONS, LEAD_SOURCE_OPTIONS } from "../../../../../../constants/leadDropdown";
import {
  TextInputComponent,
  SelectInputComponent,
  DescriptionInputComponent,
} from "../../../../../../common/components";
import { updateCustomerDetails } from "../../../../../../api/customer";
import "./customer-view-edit-view-full-info.scss";
import { validationSchema } from "./customer-view-edit-view-full-info.validation";
import isEqual from "lodash/isEqual";
import CountryCodeWithPhoneNumber from "../../../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { isOnlyCountryCode } from "../../../../../../utils/utils";
import ConfirmationPopup from "../../../../../../common/components/confirmation-popup/confirmation-popup";
import AlertIcon from "../../../../../../assets/icons/alert-icon.svg";
import { useLogin } from "../../../../../../common/hooks/useUser";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs from "dayjs";
import SearchableSelectOwner from "../../../../../../common/components/select-owner/select-owner-compoent";
import { getUserDetails } from "../../../../../../api/lead";
import AddressFormFields from "../../../../../../common/components/address-form-fields/address-form-fields";

interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

const CustomerViewEditViewFullInfo: React.FC<
  CustomerViewEditViewFullInfoProps
> = (props: any) => {
  const {
    customerDetails,
    setViewFullInfoOpen,
    selectedCustomerId,
    handleCancelUpdate,
    setAlertType,
    setAlertMessage,
    setAlertOpen,
    setLoading,
    uploadedImageFile,
    removeImage,
    resetImageUpload,
    handleFormSubmitError,
    disableCustomerStatus,
    isMobile,
  } = props;
  const formikRef: any = React.useRef(null);
   const [selectedCustomerOwner, setSelectedCustomerOwner] = useState<OwnerOption>({
      value: "",
      label: "",
      email: "",
      avatar: null,
    });
    const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
    useEffect(() => {
      if (leadOptions?.length)
        setSelectedCustomerOwner(leadOptions[0]);
    }, [leadOptions]);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const { tenantDetails, userDetails } = useLogin();
  const disablePartnerAssignmentBasedOnCustomerStatus = [
    "Closed-Won",
    "Converted",
  ];
  const [isClickedAssignPartner, setIsClickedAssignPartner] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const handleOwnerChange = (owner: any) => {
    setSelectedCustomerOwner(owner);
  };

  const checkIsAFPSuperAdmin = () => {
    if (
      tenantDetails?.tenantType === "SuperTenant" &&
      userDetails?.roles?.role === "Super Admin"
    )
      return true;
    return false;
  };
  useEffect(() => {
      const fetchCreatedByOptions = async () => {
        try {
          const response = await getUserDetails({}, "Customer Profile");
          const data = response?.data?.data?.data?.data;
          const formattedOptions: OwnerOption[] = data.map((user: any) => ({
            value: String(user.id),
            label: `${user.first_name} ${user.last_name}`,
            email: user.email,
            avatar: user.profile_image_url || null,
          }));
   
          setLeadOptions(formattedOptions);
   
           const customerOwnerId = sanitize(customerDetails?.customerOwner?.id);
           const isOwnerInOptions = formattedOptions.some(
          (opt) => opt.value === customerOwnerId
        );
        } catch (error: any) {
          setAlertMessage(
            error?.response?.data?.message ||
              "Something went wrong please try again."
          );
          setAlertType("error");
          setAlertOpen(true);
          setLoading(false);
        }
      };
   
      fetchCreatedByOptions();
    }, []);

  const checkDisableField = () => {
    return tenantDetails?.tenantType === "SuperTenant" &&
      customerDetails?.assigned_to &&
      !(customerDetails?.lead_status === "Closed-Lost")
      ? true
      : false;
  };

  const sanitize = (value: any): string => {
    return value === null || value === undefined || value === "null"
      ? ""
      : value;
  };
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingCustomerStatus, setPendingCustomerStatus] = useState<
    string | null
  >(null);
  const initialValues = useMemo(
    () => ({
      firstName: sanitize(customerDetails?.first_name),
      secondaryEmail: sanitize(customerDetails?.secondary_email_address) || null,
      lastName: sanitize(customerDetails?.last_name),
      dob: customerDetails?.dob
                    ? dayjs.utc(customerDetails?.dob).local()
                    : null,
      
      primaryPhoneNumber: sanitize(customerDetails.primary_phone_number) || null,
      middleName: sanitize(customerDetails?.middle_name),
      selectedLeadSource: customerDetails?.lead_source ?? null,
      email: sanitize(customerDetails?.primary_email_address),
      // Mailing Address
      mailingAddress:
      {
        streetLine1: sanitize(customerDetails?.mailingAddress?.street_line_1) || "",
        streetLine2: sanitize(customerDetails?.mailingAddress?.street_line_2) || "",
        city: sanitize(customerDetails?.mailingAddress?.city) || "",
        state: sanitize(customerDetails?.mailingAddress?.state) || "",
        country: sanitize(customerDetails?.mailingAddress?.country) || "",
        zipcode: sanitize(customerDetails?.mailingAddress?.zipcode) || "",
      },
      // Permanent Address
      permanentAddress: {
        streetLine1: sanitize(customerDetails?.permanentAddress?.street_line_1) || "",
        streetLine2: sanitize(customerDetails?.permanentAddress?.street_line_2) || "",
        city: sanitize(customerDetails?.permanentAddress?.city) || "",
        state: sanitize(customerDetails?.permanentAddress?.state) || "",
        country: sanitize(customerDetails?.permanentAddress?.country) || "",
        zipcode: sanitize(customerDetails?.permanentAddress?.zipcode) || "",  
      },
      description: sanitize(customerDetails?.description),
      secondaryPhoneNumber: sanitize(
        customerDetails?.secondary_phone_number
      ),
      selectedCustomerStatus: customerDetails?.lead_status || null,
      assistant: sanitize(customerDetails?.assistant) || null,
      assistant_phone_number: sanitize(
        customerDetails?.assistant_phone_number
      ) || null,
      company: sanitize(customerDetails?.company) || null,
      imageUrl: customerDetails?.image_url || null,
      preferredContactMode: customerDetails?.preferred_contact_mode ,
      customerOwner:
        sanitize(String(customerDetails?.customerOwner?.id)) ?? null,
        fax: sanitize(customerDetails?.fax_number),
    }),
    [customerDetails]
  );
  const handleCancel = () => {
    resetImageUpload?.();
    setViewFullInfoOpen(false);
  };

  const handleSave = async (values: any) => {
    console.log('mailingAddress', values?.mailingAddress);
    setLoading(true);
    try {
      const formData = new FormData();
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName || null,
         dob: values.dob
          ? new Date(values.dob).toISOString()
          : null,
        primaryPhoneNumber: isOnlyCountryCode(values.primaryPhoneNumber)
          ? null
          : values.primaryPhoneNumber || null,
        middleName: values.middleName || null,
        leadSource: values.selectedLeadSource || null,
        primaryEmail: values.email,
         secondaryEmail: values.secondaryEmail || null,
        assistant: values?.assistant || null,
        assistantPhoneNumber: values?.assistant_phone_number || null,
        company: values?.company || null,
        page:"first",
        mailingAddress: values.mailingAddress,
        permanentAddress: values.permanentAddress,
        description: values.description || null,
        secondaryPhoneNumber: isOnlyCountryCode(
          values.secondaryPhoneNumber
        )
          ? null
          : values.secondaryPhoneNumber || null,
        selectedCustomerStatus: values.selectedCustomerStatus || null,
        customerOwnerId: values.customerOwner || null,
        preferredContactMode: values.preferredContactMode || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        faxNumber: isOnlyCountryCode(values.fax) ? null : values.fax || null,
      };
       Object.entries(payload).forEach(([key, value]) => {
  if (
    key !== "mailingAddress" &&
    key !== "permanentAddress" &&
    value !== null &&
    value !== undefined
  ) {
    formData.append(key, value);
  }
});

    const appendAddressField = (form: FormData, prefix: string, key: string, value: any) => {
      if (value !== null && value !== undefined && value !== "") {
        form.append(`${prefix}[${key}]`, value);
      }
    };

    appendAddressField(formData, "mailingAddress", "streetLine1", values?.mailingAddress?.streetLine1);
    appendAddressField(formData, "mailingAddress", "streetLine2", values?.mailingAddress?.streetLine2);
    appendAddressField(formData, "mailingAddress", "city", values?.mailingAddress?.city);
    appendAddressField(formData, "mailingAddress", "state", values?.mailingAddress?.state);
    appendAddressField(formData, "mailingAddress", "country", values?.mailingAddress?.country);
    appendAddressField(formData, "mailingAddress", "zipcode", values?.mailingAddress?.zipcode);

    appendAddressField(formData, "permanentAddress", "streetLine1", values?.permanentAddress?.streetLine1);
    appendAddressField(formData, "permanentAddress", "streetLine2", values?.permanentAddress?.streetLine2);
    appendAddressField(formData, "permanentAddress", "city", values?.permanentAddress?.city);
    appendAddressField(formData, "permanentAddress", "state", values?.permanentAddress?.state);
    appendAddressField(formData, "permanentAddress", "country", values?.permanentAddress?.country);
    appendAddressField(formData, "permanentAddress", "zipcode", values?.permanentAddress?.zipcode);

      if (values.imageUrl instanceof File) {
        formData.append("customerImg", values.imageUrl);
      }
      if (uploadedImageFile) {
        formData.append("customerImg", uploadedImageFile);
      } else if (removeImage && !uploadedImageFile) {
        formData.append("removeImage", "true");
      }
      if (values.imageUrl instanceof File) {
        formData.append("customerImg", uploadedImageFile);
      }
      const response = await updateCustomerDetails(
        selectedCustomerId,
        formData
      );

      if (response.data.success) {
        if (props.onCustomerNameUpdate) {
          props.onCustomerNameUpdate(
            `${values.firstName} ${values.middleName || ""} ${values.lastName}`.trim()
          );
        }
        setAlertMessage("Customer details updated successfully");
        setAlertType("success");
        setAlertOpen(true);
        handleCancelUpdate();
        setLoading(false);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const checkAddDetails = (errors: any) => {
    Object.keys(errors)?.forEach((key) => {
      const isFromDetailsTab = ["secondaryPhoneNumber", "leadValue"]?.includes(
        key
      );
      if (isFromDetailsTab) {
        handleFormSubmitError("additionalDetails");
      }
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(true)}
      onSubmit={(values) => handleSave(values)}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        setFieldTouched,
        touched,
        errors,
      }) => {
        const isFormDirty = !isEqual(values, initialValues);

        return (
          <Form>
            <div className="customer-list-view-details-view-box-full-info">
              {!isMobile && (
              <div className="customer-list-view-details-view-box-full-info-header">
                <div>Details</div>
                <div>
                  {isViewOpen ? (
                    <i
                      className="fa fa-angle-down"
                      onClick={() => {
                        setIsViewOpen(!isViewOpen);
                      }}
                      aria-hidden="true"
                    />
                  ) : (
                    <i
                      className="fa fa-angle-up"
                      onClick={() => {
                        setIsViewOpen(!isViewOpen);
                      }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
              )}
              {isViewOpen ? (
                <>
                  <div className="customer-list-view-details-view-box-full-info-header-text">
                    Customer Information
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-data">
                    <div className="customer-list-view-details-view-box-full-info-data-section">
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          First Name
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="firstName"
                            placeholder="Enter First Name"
                            optional={true}
                            disabled={checkDisableField()}
                            value={values.firstName}
                            onChange={(e: any) =>
                              setFieldValue("firstName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Last Name
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="lastName"
                            placeholder="Enter Last Name"
                            optional={true}
                            disabled={checkDisableField()}
                            value={values.lastName}
                            onChange={(e: any) =>
                              setFieldValue("lastName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Date of Birth
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <DatePickerComponent
                            label="Date of Birth"
                            isLabel={false}
                            fieldName="dob"
                            value={
                              values?.dob
                                ? dayjs(values?.dob)
                                : null
                            }
                            onChange={(date: any) =>
                              setFieldValue("dob", date)
                            }
                            minDate={dayjs("1900-01-01")}
                            maxDate={dayjs()}
                            optional={true}
                          />
                        </div>
                      </div>

                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Phone Number
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <CountryCodeWithPhoneNumber
                            fieldName="primaryPhoneNumber"
                            placeholder={
                              values?.primaryPhoneNumber
                                ? ""
                                : "Enter Primary Phone Number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any
                            ) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object"
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("primaryPhoneNumber", null);
                              } else {
                                setFieldValue("primaryPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("primaryPhoneNumber", true);
                            }}
                            value={values?.primaryPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                            // countryCodeEditable
                            disabled={checkDisableField()}
                          />
                          {touched.primaryPhoneNumber &&
                            touched?.primaryPhoneNumber && (
                              <div className="error-message">
                                {errors.primaryPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="customer-list-view-details-view-box-full-info-data-section">
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Middle Name
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="middleName"
                            placeholder="Enter Middle Name"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.middleName}
                            onChange={(e: any) =>
                              setFieldValue("middleName", e?.target?.value)
                            }
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                      </div>
                       <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Customer Owner
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                           <SearchableSelectOwner
                            fieldName="customerOwner"
                            options={leadOptions}
                            placeholder="Select Owner"
                            optional={true}
                            value={
                              leadOptions.find(
                                (opt) => opt.value === values?.customerOwner
                              ) || null
                            }
                            onChange={(selectedOption: OwnerOption | null) => {
                              if (selectedOption) {
                                setFieldValue(
                                  "customerOwner",
                                  selectedOption.value
                                ); // saving only the ID
                              } else {
                                setFieldValue("customerOwner", null); // handle null case
                              }
                            }}
                            handleBlur={() => {
                              setFieldTouched("customerOwner", true);
                            }}
                          />
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Lead Source
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="leadSource"
                            options={LEAD_SOURCE_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("selectedLeadSource", e);
                            }}
                            value={values?.selectedLeadSource}
                            notEditable={true}
                            disabled={checkDisableField()}
                          />
                        </div>
                      </div>
                      <div className="customer-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="customer-list-view-details-view-box-full-info-data-section-main">
                          Email
                        </div>
                        <div className="customer-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="email"
                            fieldName="email"
                            placeholder="Enter Email"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.email}
                            onChange={(e: any) =>
                              setFieldValue("email", e?.target?.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-header-text">
                    Address
                  </div>
                  <div
                    style={{ flex: 1 }}
                    className="customer-address-header-text"
                    >
                    Mailing Address
                  </div>
                    <Box
                      className="tenant-basic-details-content tenant-edit-basic-details-wrapper"
                      gap={3}
                    >
                      <AddressFormFields
                        postCode="zipcode"
                        isOutlineInputs
                        disabled={checkDisableField()}
                        namePrefix="mailingAddress"
                      />
                    </Box>
                  {/* Permanent Address */}
                    <div
                      style={{ flex: 1 }}
                      className="customer-address-header-text"
                    >
                      Permanent Address
                    </div>
                    <Box
                      className="tenant-basic-details-content tenant-edit-basic-details-wrapper"
                      gap={3}
                    >
                      <AddressFormFields
                        postCode="zipcode"
                        isOutlineInputs
                        disabled={checkDisableField()}
                        namePrefix="permanentAddress"
                      />
                    </Box>
                  <div className="customer-list-view-details-view-box-full-info-header-text">
                    Description
                  </div>
                  <div className="customer-list-view-details-view-box-full-info-description-box">
                    <DescriptionInputComponent
                      fieldName="description"
                      placeholder="Add Description"
                      optional={false}
                      disabled={checkDisableField()}
                      value={values.description}
                      onChange={(e: any) =>
                        setFieldValue("description", e?.target?.value)
                      }
                      maxlength={150}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
            <Box className="footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  checkAddDetails(errors);
                  handleSubmit();
                }}
                className="cancel-button next-button-wrapper"
                disabled={
                  !(isFormDirty || uploadedImageFile || removeImage) ||
                  !!errorMessage
                }
              >
                Save
              </button>
            </Box>
            <ConfirmationPopup
              title="Update Customer Status"
              open={showConfirmation}
              note="Do you want to close this lead in the CRM"
              onCancel={() => {
                setFieldValue("selectedCustomerStatus", "Converted");
                setShowConfirmation(false);
                setPendingCustomerStatus(null);
              }}
              onOkay={() => {
                setFieldValue("selectedCustomerStatus", "Closed-Won");
                setShowConfirmation(false);
                setPendingCustomerStatus(null);
              }}
              buttonText="Confirm"
              Icon={AlertIcon}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default CustomerViewEditViewFullInfo;
